/**
 * TimesRoteamentoView — Configuração → Times & Roteamento (Item 366 Fase 4).
 *
 * O admin/gestor cria times, atribui membros (member/lead) com time primário, e
 * escreve regras de roteamento (intenção/canal → time + estratégia). É o "lar" do
 * que faltava: como uma conversa encontra o departamento certo. Mutações gateadas
 * em `team-config-manage` (times/membros) e `playbook-author` (regras); sem elas,
 * leitura. If-Match (ADR-071) nas mutações de Team — 409 propaga não-destrutivo.
 *
 * Tela nova (sem mockup Claude Design) — segue a linguagem DS das telas de Config
 * existentes (cards + chips + Modal de @lume/ui-components).
 */

import { Modal } from '@lume/ui-components';
import { useMemo, useState } from 'react';

import { useUsers } from '../../hooks/use-users';
import { ApiError } from '../../lib/api-client';
import { useToastStore } from '../../stores/toast-store';
import { usePlaybookList } from '../playbooks/use-playbooks';

import { memberOptionLabel, ruleDestinationLabel } from './routing-labels';
import { ROUTING_STRATEGY_LABELS, TEAM_AREA_LABELS } from './types';
import {
  useAddMember,
  useCreateRoutingRule,
  useCreateTeam,
  useDeleteRoutingRule,
  useDeleteTeam,
  useRemoveMember,
  useRoutingRules,
  useTeamsList,
  useUpdateMember,
} from './use-team-config';

import type { CreateTeamBody, RoutingRuleDto, RoutingStrategy, TeamArea, TeamView } from './types';

const STRATEGIES: RoutingStrategy[] = [
  'least_loaded',
  'round_robin',
  'skills_weighted',
  'fixed_route',
];

// Áreas oferecidas no seletor = as especialidades que o roteamento PREFERE.
// `general` é omitido de propósito: para o roteamento ele é idêntico a "Sem
// especialidade" (não é alvo de nenhum vertical) — oferecer ambos confundiria.
const TEAM_AREAS: TeamArea[] = [
  'support',
  'commercial',
  'financial',
  'retention',
  'legal',
  'healthcare',
];

export interface TimesRoteamentoViewProps {
  canManage: boolean;
}

export function TimesRoteamentoView({ canManage }: TimesRoteamentoViewProps): JSX.Element {
  const teams = useTeamsList();
  const rules = useRoutingRules();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [creatingRule, setCreatingRule] = useState(false);

  const selected = useMemo(
    () => teams.data.find((t) => t.id === selectedId) ?? null,
    [teams.data, selectedId],
  );

  const teamNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of teams.data) m.set(t.id, t.name);
    return m;
  }, [teams.data]);
  // A-03 (noturna 04/Set/2026): regra que roteia para playbook mostrava «—».
  const playbooks = usePlaybookList();
  const playbookNameById = useMemo(
    () => new Map(playbooks.data.map((pb) => [pb.id, pb.name] as const)),
    [playbooks.data],
  );

  if (teams.isLoading) {
    return <p className="tr-muted">Carregando times…</p>;
  }
  if (teams.isError) {
    return (
      <p className="tr-banner" role="alert">
        Falha ao carregar os times.
      </p>
    );
  }

  return (
    <div className="times-roteamento-screen">
      <div className="tr-page">
        <header className="tr-head">
          <div>
            <h1 className="tr-title">Times &amp; Roteamento</h1>
            <p className="tr-subtitle">
              Agrupe operadores em times, defina líderes e escreva as regras que levam cada conversa
              ao time certo.
            </p>
          </div>
          {canManage && (
            <button
              type="button"
              className="tr-btn tr-btn--primary"
              onClick={() => setCreatingTeam(true)}
            >
              + Novo time
            </button>
          )}
        </header>

        <div className="tr-layout">
          {/* Coluna times */}
          <section className="tr-col">
            <h2 className="tr-col-title">Times ({teams.data.length})</h2>
            {teams.data.length === 0 && <p className="tr-muted">Nenhum time ainda.</p>}
            <ul className="tr-team-list">
              {teams.data.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    className={`tr-team-card${t.id === selectedId ? ' tr-team-card--active' : ''}`}
                    onClick={() => setSelectedId(t.id)}
                  >
                    <span className="tr-team-name">
                      {t.name}
                      {!t.active && <span className="tr-pill tr-pill--muted">inativo</span>}
                    </span>
                    <span className="tr-team-meta">
                      {t.memberCount} membro(s) ·{' '}
                      {t.routingStrategy
                        ? ROUTING_STRATEGY_LABELS[t.routingStrategy]
                        : 'estratégia herdada'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          {/* Coluna detalhe */}
          <section className="tr-col tr-col--detail">
            {selected ? (
              <TeamDetail
                team={selected}
                canManage={canManage}
                onDeleted={() => setSelectedId(null)}
              />
            ) : (
              <p className="tr-muted">Selecione um time para ver membros e ações.</p>
            )}
          </section>
        </div>

        {/* Regras de roteamento */}
        <section className="tr-rules">
          <div className="tr-rules-head">
            <h2 className="tr-col-title">Regras de roteamento ({rules.data.length})</h2>
            {canManage && rules.canRead && (
              <button type="button" className="tr-btn" onClick={() => setCreatingRule(true)}>
                + Nova regra
              </button>
            )}
          </div>
          {!rules.canRead ? (
            <p className="tr-muted">Sem acesso às regras de roteamento.</p>
          ) : rules.data.length === 0 ? (
            <p className="tr-muted">
              Nenhuma regra. Sem regra, a conversa cai no time default do tenant (ou roteia entre
              todos os atendentes).
            </p>
          ) : (
            <table className="tr-rules-table">
              <thead>
                <tr>
                  <th>Prioridade</th>
                  <th>Nome</th>
                  <th>Condições</th>
                  <th>Destino</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {[...rules.data]
                  .sort((a, b) => a.priority - b.priority)
                  .map((r) => (
                    <RuleRow
                      key={r.id}
                      rule={r}
                      teamName={ruleDestinationLabel(r, teamNameById, playbookNameById)}
                      canManage={canManage}
                    />
                  ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {creatingTeam && <CreateTeamModal onClose={() => setCreatingTeam(false)} />}
      {creatingRule && (
        <CreateRuleModal teams={teams.data} onClose={() => setCreatingRule(false)} />
      )}
    </div>
  );
}

// ─── Detalhe do time (membros + editar/excluir) ──────────────────────────────

function TeamDetail({
  team,
  canManage,
  onDeleted,
}: {
  team: TeamView;
  canManage: boolean;
  onDeleted: () => void;
}): JSX.Element {
  const [addingMember, setAddingMember] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const removeMember = useRemoveMember();
  const updateMember = useUpdateMember();
  const deleteTeam = useDeleteTeam();
  const toast = useToastStore((s) => s.push);

  const handleDelete = (): void => {
    deleteTeam.mutate(
      { id: team.id, expectedVersion: team.version },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: `Time "${team.name}" descontinuado`,
            durationMs: 3000,
          });
          setConfirmDelete(false);
          onDeleted();
        },
        onError: (err) => toast({ variant: 'error', title: describeError(err), durationMs: 4000 }),
      },
    );
  };

  const toggleLead = (userId: string, currentRole: string): void => {
    updateMember.mutate({
      teamId: team.id,
      userId,
      body: { roleInTeam: currentRole === 'lead' ? 'member' : 'lead' },
    });
  };

  return (
    <div className="tr-detail">
      <div className="tr-detail-head">
        <h2 className="tr-detail-title">{team.name}</h2>
        <code className="tr-slug">{team.slug}</code>
      </div>
      {team.description && <p className="tr-desc">{team.description}</p>}
      <p className="tr-detail-meta">
        Estratégia:{' '}
        <strong>
          {team.routingStrategy
            ? ROUTING_STRATEGY_LABELS[team.routingStrategy]
            : 'herdada do tenant'}
        </strong>
      </p>
      <p className="tr-detail-meta">
        Área: <strong>{team.area ? TEAM_AREA_LABELS[team.area] : 'sem especialidade'}</strong>
      </p>

      <div className="tr-members-head">
        <h3 className="tr-members-title">Membros ({team.memberCount})</h3>
        {canManage && (
          <button type="button" className="tr-btn tr-btn--sm" onClick={() => setAddingMember(true)}>
            + Adicionar
          </button>
        )}
      </div>
      {team.members.length === 0 ? (
        <p className="tr-muted">
          Time sem membros. Adicione operadores para ele receber conversas.
        </p>
      ) : (
        <ul className="tr-member-list">
          {team.members.map((m) => (
            <li key={m.userId} className="tr-member">
              <span className="tr-member-name">
                {m.name}
                {m.roleInTeam === 'lead' && <span className="tr-pill tr-pill--lead">líder</span>}
                {m.isPrimary && <span className="tr-pill">primário</span>}
              </span>
              {canManage && (
                <span className="tr-member-actions">
                  <button
                    type="button"
                    className="tr-link"
                    onClick={() => toggleLead(m.userId, m.roleInTeam)}
                  >
                    {m.roleInTeam === 'lead' ? 'Tornar membro' : 'Tornar líder'}
                  </button>
                  <button
                    type="button"
                    className="tr-link tr-link--danger"
                    onClick={() => removeMember.mutate({ teamId: team.id, userId: m.userId })}
                  >
                    Remover
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <div className="tr-detail-footer">
          <button
            type="button"
            className="tr-link tr-link--danger"
            onClick={() => setConfirmDelete(true)}
          >
            Descontinuar time
          </button>
        </div>
      )}

      {addingMember && <AddMemberModal team={team} onClose={() => setAddingMember(false)} />}
      <Modal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Descontinuar "${team.name}"?`}
        destructive
        confirmLabel="Descontinuar"
        onConfirm={handleDelete}
      >
        <p>
          O time fica inativo (soft-delete) — o histórico de conversas, regras e handoffs é
          preservado. As regras que apontam para ele deixam de rotear.
        </p>
      </Modal>
    </div>
  );
}

// ─── Modais ──────────────────────────────────────────────────────────────────

function CreateTeamModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [strategy, setStrategy] = useState<RoutingStrategy | ''>('');
  const [area, setArea] = useState<TeamArea | ''>('');
  const create = useCreateTeam();
  const toast = useToastStore((s) => s.push);

  const submit = (): void => {
    if (name.trim().length < 2) return;
    const body: CreateTeamBody = { name: name.trim() };
    if (description.trim()) body.description = description.trim();
    if (strategy) body.routingStrategy = strategy;
    if (area) body.area = area;
    create.mutate(body, {
      onSuccess: (t) => {
        toast({ variant: 'success', title: `Time "${t.name}" criado`, durationMs: 3000 });
        onClose();
      },
      onError: (err) => toast({ variant: 'error', title: describeError(err), durationMs: 4000 }),
    });
  };

  return (
    <Modal isOpen onClose={onClose} title="Novo time" confirmLabel="Criar" onConfirm={submit}>
      <label className="tr-field">
        <span>Nome</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ex.: Suporte N1"
        />
      </label>
      <label className="tr-field">
        <span>Descrição (opcional)</span>
        <input value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <label className="tr-field">
        <span>Estratégia de distribuição</span>
        <select
          value={strategy}
          onChange={(e) => setStrategy(e.target.value as RoutingStrategy | '')}
        >
          <option value="">Herdar do tenant</option>
          {STRATEGIES.map((s) => (
            <option key={s} value={s}>
              {ROUTING_STRATEGY_LABELS[s]}
            </option>
          ))}
        </select>
      </label>
      <label className="tr-field">
        <span>Área (especialidade)</span>
        <select value={area} onChange={(e) => setArea(e.target.value as TeamArea | '')}>
          <option value="">Sem especialidade</option>
          {TEAM_AREAS.map((a) => (
            <option key={a} value={a}>
              {TEAM_AREA_LABELS[a]}
            </option>
          ))}
        </select>
        <small className="tr-muted">
          O roteamento prefere o time da área que casa com o assunto do atendimento.
        </small>
      </label>
    </Modal>
  );
}

function AddMemberModal({ team, onClose }: { team: TeamView; onClose: () => void }): JSX.Element {
  const usersQuery = useUsers();
  const add = useAddMember();
  const toast = useToastStore((s) => s.push);
  const [userId, setUserId] = useState('');
  const [asLead, setAsLead] = useState(false);
  const [asPrimary, setAsPrimary] = useState(false);

  const memberIds = new Set(team.members.map((m) => m.userId));
  const candidates = (usersQuery.data?.data ?? []).filter(
    (u) => !memberIds.has(u.id) && u.status === 'active',
  );

  const submit = (): void => {
    if (!userId) return;
    add.mutate(
      {
        teamId: team.id,
        body: {
          userId,
          ...(asLead && { roleInTeam: 'lead' as const }),
          ...(asPrimary && { isPrimary: true }),
        },
      },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Membro adicionado', durationMs: 3000 });
          onClose();
        },
        onError: (err) => toast({ variant: 'error', title: describeError(err), durationMs: 4000 }),
      },
    );
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Adicionar membro — ${team.name}`}
      confirmLabel="Adicionar"
      onConfirm={submit}
    >
      <label className="tr-field">
        <span>Operador</span>
        <select value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Selecione…</option>
          {candidates.map((u) => (
            <option key={u.id} value={u.id}>
              {memberOptionLabel(u)}
            </option>
          ))}
        </select>
      </label>
      <label className="tr-check">
        <input type="checkbox" checked={asLead} onChange={(e) => setAsLead(e.target.checked)} />{' '}
        Líder do time
      </label>
      <label className="tr-check">
        <input
          type="checkbox"
          checked={asPrimary}
          onChange={(e) => setAsPrimary(e.target.checked)}
        />{' '}
        Time primário deste operador
      </label>
    </Modal>
  );
}

function CreateRuleModal({
  teams,
  onClose,
}: {
  teams: TeamView[];
  onClose: () => void;
}): JSX.Element {
  const create = useCreateRoutingRule();
  const toast = useToastStore((s) => s.push);
  const [name, setName] = useState('');
  const [priority, setPriority] = useState('10');
  const [intent, setIntent] = useState('');
  const [channel, setChannel] = useState('');
  const [teamId, setTeamId] = useState('');

  const submit = (): void => {
    if (name.trim().length < 2 || !teamId) return;
    const conditions: Record<string, unknown> = {};
    if (intent.trim())
      conditions.intent = intent
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    if (channel.trim()) conditions.channel = channel.trim();
    create.mutate(
      {
        name: name.trim(),
        priority: Number.parseInt(priority, 10) || 10,
        conditions,
        decisionTargetTeamId: teamId,
      },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: `Regra "${name.trim()}" criada`, durationMs: 3000 });
          onClose();
        },
        onError: (err) => toast({ variant: 'error', title: describeError(err), durationMs: 4000 }),
      },
    );
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Nova regra de roteamento"
      confirmLabel="Criar"
      onConfirm={submit}
    >
      <label className="tr-field">
        <span>Nome</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ex.: Técnico → Suporte N1"
        />
      </label>
      <label className="tr-field">
        <span>Prioridade (menor = avaliada antes)</span>
        <input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
      </label>
      <label className="tr-field">
        <span>Intenções (separadas por vírgula, opcional)</span>
        <input
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          placeholder="suporte_tecnico, conectividade"
        />
      </label>
      <label className="tr-field">
        <span>Canal (opcional)</span>
        <input
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          placeholder="whatsapp"
        />
      </label>
      <label className="tr-field">
        <span>Time destino</span>
        <select value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">Selecione…</option>
          {teams
            .filter((t) => t.active)
            .map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
        </select>
      </label>
    </Modal>
  );
}

function RuleRow({
  rule,
  teamName,
  canManage,
}: {
  rule: RoutingRuleDto;
  teamName: string;
  canManage: boolean;
}): JSX.Element {
  const del = useDeleteRoutingRule();
  const toast = useToastStore((s) => s.push);
  const cond = summarizeConditions(rule.conditions);
  return (
    <tr>
      <td>{rule.priority}</td>
      <td>{rule.name}</td>
      <td className="tr-cond">{cond}</td>
      <td>{teamName}</td>
      <td>
        {canManage && (
          <button
            type="button"
            className="tr-link tr-link--danger"
            onClick={() =>
              del.mutate(
                { id: rule.id, expectedVersion: rule.version },
                {
                  onSuccess: () =>
                    toast({ variant: 'success', title: 'Regra removida', durationMs: 2500 }),
                  onError: (err) =>
                    toast({ variant: 'error', title: describeError(err), durationMs: 4000 }),
                },
              )
            }
          >
            Remover
          </button>
        )}
      </td>
    </tr>
  );
}

function summarizeConditions(conditions: Record<string, unknown>): string {
  const parts: string[] = [];
  if (Array.isArray(conditions.intent))
    parts.push(`intenção: ${(conditions.intent as string[]).join('/')}`);
  if (typeof conditions.channel === 'string') parts.push(`canal: ${conditions.channel}`);
  if (typeof conditions.classificationLevel === 'number')
    parts.push(`nível ${conditions.classificationLevel}`);
  return parts.length > 0 ? parts.join(' · ') : 'qualquer conversa';
}

function describeError(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) {
    return 'Conflito de versão — recarregue e tente de novo.';
  }
  if (err instanceof ApiError && err.status === 400) {
    return 'Dados inválidos — revise os campos.';
  }
  return 'Não foi possível concluir a ação.';
}
